import axios from "axios";
import {environmentConfig} from "@/config/enviromentConfig";
import store from "@/store";
import {getCaptchaToken, CAPTCHA_WIDGET_ID} from "@/functions/turnstile";
import {telemetryHeader} from "@/functions/telemetryCollector";

const api = axios.create({baseURL: environmentConfig.triviaV2Service});

let attemptToken = null;

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error && error.response && error.response.status;
        const url = (error && error.config && error.config.url) || '';
        if (status === 401 && !url.includes('/api/session')) {
            localStorage.removeItem('token');
            store.commit('setToken', undefined);
            store.commit('setUser', undefined);
            if (window.location.pathname !== '/landing') window.location.href = '/landing';
        }
        return Promise.reject(error);
    }
);

async function captchaHeaders(action) {
    const headers = {};
    const token = await getCaptchaToken(action);
    if (token) {
        headers['X-Captcha-Token'] = token;
        headers['X-Captcha-Widget'] = CAPTCHA_WIDGET_ID;
        headers['X-Captcha-Action'] = action;
    }
    return headers;
}

function attemptHeaders() {
    return attemptToken ? {'X-Attempt-Token': attemptToken} : {};
}

function captureAttemptToken(data) {
    const token = Array.isArray(data) && data[0] ? data[0].attemptToken : undefined;
    if (token) attemptToken = token;
}

function storeAuth(data) {
    if (data && data.token) {
        localStorage.setItem('token', data.token);
        store.commit('setToken', data.token);
    }
    return data;
}

export default class TriviaV2Service {
    static async sessionLogin(msisdn) {
        const headers = await captchaHeaders('create_session');
        const response = await api.post('/api/dcb/create-session-id', {
            msisdn,
            serviceId: store.state.brandConfig.serviceId,
        }, {headers});
        return storeAuth(response.data);
    }

    static async decryptMsisdnLogin(encryptedMsisdn) {
        const headers = await captchaHeaders('decrypt_msisdn');
        const response = await api.post('/api/encryption/decrypt-msisdn', {encryptedMsisdn}, {headers});
        return storeAuth(response.data);
    }

    static async getSession() {
        try {
            const response = await api.get('/api/session');
            return response.data;
        } catch (e) {
            return null;
        }
    }

    static async getQuestions(gameStart) {
        const captcha = gameStart ? await captchaHeaders('game_start') : {};
        const response = await api.get('/api/game/get-questions', {
            params: {
                amountOfQuestions: 1,
                questionCategoryId: 52,
                difficulty: 1,
                startTime: new Date().toISOString(),
                productId: store.state.brandConfig.productId,
            },
            headers: {...attemptHeaders(), ...captcha},
        });
        captureAttemptToken(response.data);
        return response;
    }

    static async saveAnswer(answer, gameId, finalAnswer) {
        const captcha = finalAnswer ? await captchaHeaders('game_end') : {};
        return api.post('/api/game/save-answer', {
            gameId,
            productId: store.state.brandConfig.productId,
            service: store.state.brandConfig.serviceId,
            answer,
        }, {headers: {...attemptHeaders(), ...telemetryHeader(), ...captcha}});
    }

    static async getLeaderboard() {
        return api.get('/api/game/get-leaderboard', {
            params: {
                productId: store.state.brandConfig.productId,
                numberRows: 15,
            },
        });
    }

    static async getUserState() {
        return api.get('/api/game/get-user-state', {
            params: {productId: store.state.brandConfig.productId},
        });
    }

    static async getUserStats() {
        const response = await api.get('/api/game/get-user-stats', {
            params: {
                productId: store.state.brandConfig.productId,
                service: store.state.brandConfig.serviceId,
            },
        });
        const recordsets = response.data;
        return (Array.isArray(recordsets) && recordsets[0] && recordsets[0][0]) || null;
    }

    static async upsertUsername(username) {
        return api.post('/api/game/upsert-username', {username});
    }
}
